import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

interface SeasonRecord { season_id: number; season_year: string; }
interface Member { player_id: number; full_name: string; }
interface Team { team_id: number; team_name: string; team_color?: string | null; sort_order: number; members: Member[]; }
interface Rules {
  regular_team_points_18: number; regular_team_points_9: number; tournament_team_points_per_round: number;
  min_players_per_team: number; team_competition_enabled: boolean; source: 'custom' | 'default';
}
interface TeamData {
  season_id: number | null; effective_season_id: number | null; inherited: boolean;
  teams: Team[]; unassigned: Member[]; rules: Rules;
}

const C = {
  blue: '#2563eb', blueDark: '#1e40af', slate: '#1e293b', muted: '#64748b',
  line: '#e2e8f0', bg: '#f8fafc', soft: '#f1f5f9', red: '#dc2626',
};

const s = {
  page: { background: C.bg, minHeight: '100vh', padding: '24px 16px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" } as React.CSSProperties,
  shell: { maxWidth: 920, margin: '0 auto' } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 800, color: C.slate, margin: 0 } as React.CSSProperties,
  subtitle: { color: C.muted, fontSize: 14, margin: '4px 0 22px' } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, border: `1px solid ${C.line}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 18, overflow: 'hidden' } as React.CSSProperties,
  cardHead: { padding: '14px 18px', borderBottom: `1px solid ${C.soft}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' } as React.CSSProperties,
  cardTitle: { fontSize: 16, fontWeight: 700, color: C.slate, margin: 0 } as React.CSSProperties,
  body: { padding: 18 } as React.CSSProperties,
  label: { fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.03em' } as React.CSSProperties,
  input: { padding: '9px 11px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 14, color: C.slate, background: '#fff', outline: 'none', boxSizing: 'border-box' } as React.CSSProperties,
  sel: { padding: '8px 11px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 14, background: '#fff', color: C.slate } as React.CSSProperties,
  btn: { padding: '8px 14px', borderRadius: 8, border: 'none', background: C.blue, color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 } as React.CSSProperties,
  btnGhost: { padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff', color: C.slate, cursor: 'pointer', fontWeight: 600, fontSize: 13 } as React.CSSProperties,
  btnDanger: { padding: '6px 10px', borderRadius: 8, border: `1px solid #fecaca`, background: '#fff', color: C.red, cursor: 'pointer', fontWeight: 600, fontSize: 12 } as React.CSSProperties,
  err: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14, cursor: 'pointer' } as React.CSSProperties,
  note: { background: '#eff6ff', color: '#1e3a5f', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 } as React.CSSProperties,
  empty: { padding: '24px 18px', textAlign: 'center', color: '#94a3b8', fontSize: 14 } as React.CSSProperties,
  teamGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 } as React.CSSProperties,
  teamCard: { border: `1px solid ${C.line}`, borderRadius: 10, padding: 14, background: '#fff' } as React.CSSProperties,
  teamHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 } as React.CSSProperties,
  chip: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: C.soft, borderRadius: 8, padding: '6px 10px', fontSize: 13, color: C.slate, marginBottom: 6 } as React.CSSProperties,
  x: { border: 'none', background: 'transparent', color: C.red, cursor: 'pointer', fontWeight: 800, fontSize: 16, lineHeight: 1 } as React.CSSProperties,
  num: { width: 70, padding: '7px 9px', border: `1px solid #cbd5e1`, borderRadius: 8, textAlign: 'right', fontSize: 14 } as React.CSSProperties,
  rankCell: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: C.muted, fontWeight: 700 } as React.CSSProperties,
  ranks: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' } as React.CSSProperties,
  badge: (src: string): React.CSSProperties => ({ display: 'inline-block', fontSize: 11, fontWeight: 800, padding: '2px 9px', borderRadius: 999, textTransform: 'uppercase', background: src === 'custom' ? '#dcfce7' : C.soft, color: src === 'custom' ? '#166534' : C.muted }),
};

const Teams: React.FC = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const APIURL = process.env.REACT_APP_API_URL;
  const authHeader = useCallback(async () => ({ Authorization: `Bearer ${await getAccessTokenSilently()}` }), [getAccessTokenSilently]);

  const [seasons, setSeasons] = useState<SeasonRecord[]>([]);
  const [scope, setScope] = useState<string>(''); // '' = global default, else season_id
  const [data, setData] = useState<TeamData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [rules, setRules] = useState<Rules | null>(null);
  const [savingRules, setSavingRules] = useState(false);

  const scopeBody = scope === '' ? { season_id: null } : { season_id: Number(scope) };

  const load = useCallback(async () => {
    try {
      const headers = await authHeader();
      const q = scope === '' ? '' : `?season_id=${scope}`;
      const { data } = await axios.get<TeamData>(`${APIURL}/team-competition${q}`, { headers });
      setData(data);
      setRules(data.rules);
    } catch {
      setError('Failed to load team setup.');
    }
  }, [APIURL, authHeader, scope]);

  useEffect(() => {
    if (!isAuthenticated) { if (!isLoading) loginWithRedirect(); return; }
    (async () => {
      try {
        const headers = await authHeader();
        const { data } = await axios.get<SeasonRecord[]>(`${APIURL}/seasons`, { headers });
        setSeasons(data);
      } catch { /* seasons optional for global scope */ }
    })();
  }, [isAuthenticated, isLoading, loginWithRedirect, authHeader, APIURL]);

  useEffect(() => { if (isAuthenticated) load(); }, [isAuthenticated, load]);

  const guard = async (fn: () => Promise<any>) => {
    try { await fn(); await load(); }
    catch (e: any) { setError(e?.response?.data?.error || 'Action failed.'); }
  };

  const createTeam = () => {
    const name = newTeamName.trim();
    if (!name) return;
    guard(async () => {
      const headers = await authHeader();
      await axios.post(`${APIURL}/team-competition/teams`, { ...scopeBody, team_name: name }, { headers });
      setNewTeamName('');
    });
  };

  const renameTeam = (team: Team) => {
    const name = window.prompt('Rename team', team.team_name);
    if (name == null || !name.trim() || name.trim() === team.team_name) return;
    guard(async () => {
      const headers = await authHeader();
      await axios.put(`${APIURL}/team-competition/teams/${team.team_id}`, { team_name: name.trim() }, { headers });
    });
  };

  const deleteTeam = (team: Team) => {
    if (!window.confirm(`Delete team "${team.team_name}"? Its players become unassigned.`)) return;
    guard(async () => {
      const headers = await authHeader();
      await axios.delete(`${APIURL}/team-competition/teams/${team.team_id}`, { headers });
    });
  };

  const setMembers = (team: Team, player_ids: number[]) =>
    guard(async () => {
      const headers = await authHeader();
      await axios.put(`${APIURL}/team-competition/teams/${team.team_id}/members`, { player_ids }, { headers });
    });

  const removeMember = (team: Team, pid: number) =>
    setMembers(team, team.members.filter(m => m.player_id !== pid).map(m => m.player_id));

  const addMember = (team: Team, pid: number) =>
    setMembers(team, [...team.members.map(m => m.player_id), pid]);

  const saveRules = () => {
    if (!rules) return;
    setSavingRules(true);
    guard(async () => {
      const headers = await authHeader();
      await axios.put(`${APIURL}/team-competition/rules`, { ...scopeBody, ...rules }, { headers });
    }).finally(() => setSavingRules(false));
  };

  if (!isAuthenticated) return null;

  const inherited = !!data?.inherited;

  return (
    <div style={s.page}>
      <div style={s.shell}>
        <h1 style={s.title}>Teams</h1>
        <p style={s.subtitle}>
          Configure the team competition — create teams, assign players, and set the rules that decide
          when team points are awarded. A player can belong to one team per scope.
        </p>

        {error && <div style={s.err} onClick={() => setError(null)}>{error} (dismiss)</div>}

        {/* Scope picker */}
        <div style={s.card}>
          <div style={s.body}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={s.label}>Scope</span>
                <select style={s.sel} value={scope} onChange={e => setScope(e.target.value)}>
                  <option value="">Global default (all seasons)</option>
                  {seasons.map(se => <option key={se.season_id} value={se.season_id}>{se.season_year}</option>)}
                </select>
              </div>
              <span style={{ fontSize: 13, color: C.muted }}>
                {scope === ''
                  ? 'These teams apply to any season that has not defined its own.'
                  : inherited
                    ? 'This season inherits the global default. Create a team below to give it its own roster.'
                    : 'This season has its own teams (overrides the global default).'}
              </span>
            </div>
          </div>
        </div>

        {scope !== '' && inherited && (
          <div style={s.note}>
            Showing the <strong>global default</strong> roster. Creating a team here starts a
            season-specific setup for <strong>{seasons.find(se => String(se.season_id) === scope)?.season_year}</strong>,
            which then fully replaces the default for that season.
          </div>
        )}

        {/* Teams */}
        <div style={s.card}>
          <div style={s.cardHead}>
            <h2 style={s.cardTitle}>Teams</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={s.input} placeholder="New team name" value={newTeamName}
                onChange={e => setNewTeamName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createTeam()} />
              <button style={s.btn} onClick={createTeam}>+ Add team</button>
            </div>
          </div>
          <div style={s.body}>
            {!data || data.teams.length === 0 ? (
              <div style={s.empty}>No teams yet. Add one above to get started.</div>
            ) : (
              <div style={s.teamGrid}>
                {data.teams.map(team => (
                  <div key={team.team_id} style={s.teamCard}>
                    <div style={s.teamHead}>
                      <strong style={{ fontSize: 15, color: C.slate }}>{team.team_name}</strong>
                      <span style={{ display: 'flex', gap: 6 }}>
                        <button style={s.btnGhost} onClick={() => renameTeam(team)}>Rename</button>
                        <button style={s.btnDanger} onClick={() => deleteTeam(team)}>Delete</button>
                      </span>
                    </div>
                    {team.members.length === 0
                      ? <div style={{ ...s.empty, padding: '10px 0' }}>No players</div>
                      : team.members.map(m => (
                        <div key={m.player_id} style={s.chip}>
                          <span>{m.full_name}</span>
                          <button style={s.x} title="Remove" onClick={() => removeMember(team, m.player_id)}>×</button>
                        </div>
                      ))}
                    {data.unassigned.length > 0 && (
                      <select style={{ ...s.sel, width: '100%', marginTop: 8 }} value=""
                        onChange={e => { if (e.target.value) addMember(team, Number(e.target.value)); }}>
                        <option value="">+ Add player…</option>
                        {data.unassigned.map(p => <option key={p.player_id} value={p.player_id}>{p.full_name}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )}
            {data && data.unassigned.length > 0 && (
              <div style={{ marginTop: 14, fontSize: 13, color: C.muted }}>
                <strong>Unassigned:</strong> {data.unassigned.map(p => p.full_name).join(', ')}
              </div>
            )}
          </div>
        </div>

        {/* Rules */}
        {rules && (
          <div style={s.card}>
            <div style={s.cardHead}>
              <h2 style={s.cardTitle}>Rules &amp; points <span style={s.badge(rules.source)}>{rules.source}</span></h2>
            </div>
            <div style={s.body}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: C.slate, marginBottom: 16 }}>
                <input type="checkbox" checked={rules.team_competition_enabled}
                  onChange={e => setRules({ ...rules, team_competition_enabled: e.target.checked })} />
                Team competition enabled (award team points for team rounds)
              </label>
              <div style={s.ranks}>
                <label style={s.rankCell}>Min players / team
                  <input type="number" min={1} style={s.num} value={rules.min_players_per_team}
                    onChange={e => setRules({ ...rules, min_players_per_team: Math.max(1, Math.round(Number(e.target.value) || 0)) })} /></label>
                <label style={s.rankCell}>18-hole win pts
                  <input type="number" min={0} style={s.num} value={rules.regular_team_points_18}
                    onChange={e => setRules({ ...rules, regular_team_points_18: Math.max(0, Math.round(Number(e.target.value) || 0)) })} /></label>
                <label style={s.rankCell}>9-hole win pts
                  <input type="number" min={0} style={s.num} value={rules.regular_team_points_9}
                    onChange={e => setRules({ ...rules, regular_team_points_9: Math.max(0, Math.round(Number(e.target.value) || 0)) })} /></label>
                <label style={s.rankCell}>Major pts / round
                  <input type="number" min={0} style={s.num} value={rules.tournament_team_points_per_round}
                    onChange={e => setRules({ ...rules, tournament_team_points_per_round: Math.max(0, Math.round(Number(e.target.value) || 0)) })} /></label>
              </div>
              <p style={{ fontSize: 12.5, color: C.muted, margin: '14px 0 0' }}>
                A team round is valid when at least two teams each field the minimum number of players; the team with
                the lowest net total wins. A tie for lowest net awards no team points.
              </p>
              <div style={{ marginTop: 16 }}>
                <button style={{ ...s.btn, opacity: savingRules ? 0.6 : 1 }} onClick={saveRules} disabled={savingRules}>
                  {savingRules ? 'Saving…' : (scope === '' ? 'Save default rules' : 'Save season rules')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Teams;
